import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef, useState} from 'react'
import {Lang} from 'yougile-lang'
import {CustomDataField, Hub} from 'yougile-model'
import {
  TwContentContainer,
  TwEmptyState,
  TwFieldRow,
  TwHeaderCellLeft,
  TwHeaderCellRight,
  TwHeaderRow,
  TwMainContainer,
  TwSettingsSectionTitle
} from '@components/board/crm/custom-fields-components'
import {
  CustomFieldsContext,
  CustomFieldsStore,
  useCustomFieldsStore
} from '@components/board/crm/custom-fields-store'
import {IconButtonNew, LinkButtonNew} from '@components/common/base/buttons'
import {ActionsPanel} from '@components/common/base/buttons/actions-panel/actions-panel'
import {OptionsItem} from '@components/common/base/dropdown-button-menu/types'
import {Icon} from '@components/common/base/icon'
import {InputNew} from '@components/common/base/input-new'
import {SelectWithDropdown} from '@components/common/base/select-with-dropdown'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {updateRef} from '@containers/element-utils'
import {
  boardSettingsState,
  boardSettingsStore
} from '../board-settings/board-settings-store'

export interface CustomFieldsEditorBoardSettingsTabInfo {
  addDraftFieldOnInit?: boolean
}

const getFieldTypeOptions = (): OptionsItem<CustomDataField['type']>[] => [
  {
    key: 'string',
    text: Lang.msg.crm.boardSettings.customFieldsType.string,
    icon: <Icon icon="IconLetterCase" />
  },
  {
    key: 'number',
    text: Lang.msg.crm.boardSettings.customFieldsType.number,
    icon: <Icon icon="IconHash" />
  },
  {
    key: 'email',
    text: Lang.msg.crm.boardSettings.customFieldsType.email,
    icon: <Icon icon="IconAt" />
  },
  {
    key: 'url',
    text: Lang.msg.crm.boardSettings.customFieldsType.url,
    icon: <Icon icon="IconLink" />
  }
]

export const CustomFieldsEditorBase = observer(() => {
  const [, store] = useCustomFieldsStore()
  const fieldTypeOptions = getFieldTypeOptions()
  const lastInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLElement>(null)

  useEffect(() => {
    lastInputRef.current?.focus()
  }, [store.draftFields.length])

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [store.draftFields.length])

  useEffect(() => {
    if (boardSettingsState.additionalTabInfo.addDraftFieldOnInit) {
      store.handleAdd()
      boardSettingsStore.removeFromAdditionalTabInfo('addDraftFieldOnInit')
    }
  }, [])

  return (
    <TwMainContainer>
      <TwSettingsSectionTitle>
        {Lang.msg.crm.boardSettings.settingsCustomFieldsTitle}
      </TwSettingsSectionTitle>

      <TwContentContainer>
        {store.draftFields.length > 0 && (
          <TwHeaderRow>
            <TwHeaderCellLeft>
              {Lang.msg.crm.boardSettings.fieldName}
              <span className="text-status-error">*</span>
            </TwHeaderCellLeft>
            <TwHeaderCellRight>
              {Lang.msg.crm.boardSettings.fieldType}
              <span className="text-status-error">*</span>
            </TwHeaderCellRight>
          </TwHeaderRow>
        )}

        <WithPerfectScrollbar
          className="pr-12"
          containerRef={x => updateRef(x, containerRef)}
        >
          {store.draftFields.length > 0 ? (
            store.draftFields.map((field, index) => {
              const isLast = index === store.draftFields.length - 1
              const inputRef = isLast ? lastInputRef : undefined
              return (
                <TwFieldRow key={field.id}>
                  <InputNew
                    className="flex-[2]"
                    inputClassName="hover:shadow-border-1 hover:shadow-line-base"
                    value={field.label}
                    placeholder={Lang.msg.empty}
                    onChange={val => store.handleChange(field.id, 'label', val)}
                    error={
                      store.wasSubmitted && store.invalidIds.includes(field.id)
                    }
                    ref={inputRef}
                  />

                  <SelectWithDropdown
                    options={fieldTypeOptions}
                    value={
                      fieldTypeOptions.find(opt => opt.key === field.type)?.text
                    }
                    size="28"
                    withIcons
                    onChange={opt =>
                      store.handleChange(field.id, 'type', opt.key)
                    }
                    error={store.invalidIds.includes(field.id)}
                    className="flex-1 rounded-8"
                  />

                  <IconButtonNew
                    icon="IconTrash"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => store.handleRemove(field.id)}
                  />
                </TwFieldRow>
              )
            })
          ) : (
            <TwEmptyState>
              {Lang.msg.crm.boardSettings.noCustomFields}
            </TwEmptyState>
          )}

          <LinkButtonNew
            icon="plus"
            className="my-8 mx-12"
            onClick={store.handleAdd}
            title={Lang.msg.crm.boardSettings.addNewField}
          />
        </WithPerfectScrollbar>
      </TwContentContainer>

      <ActionsPanel
        className="my-8 ml-16 mr-24 !static"
        show={boardSettingsState.hasChanges}
        primaryClick={store.handleSave}
        primaryText={Lang.msg.save}
        secondaryClick={store.handleCancel}
        secondaryText={Lang.msg.cancel}
        error={store.leaveWithoutSave}
        errorText={Lang.msg.errorNotSaveData}
        errorBg={store.errorBg}
      />
    </TwMainContainer>
  )
})

interface CustomFieldsEditorProps {
  project: Hub
}

export const CustomFieldsEditor = observer(
  ({project}: CustomFieldsEditorProps) => {
    const [store] = useState(() => new CustomFieldsStore(project))

    useEffect(() => {
      return () => {
        store.handleCancel()
      }
    }, [store])

    return (
      <CustomFieldsContext.Provider value={store}>
        <CustomFieldsEditorBase />
      </CustomFieldsContext.Provider>
    )
  }
)
